import React from 'react';
import classNames from 'classnames';
import { SectionProps } from '../../utils/SectionProps';
import { Link } from 'react-router-dom';
import SectionHeader from './partials/SectionHeader';
import Input from '../elements/Input';
import Button from '../elements/Button';
import api from "../../api/http";
import { API_ENDPOINTS } from '../../api/endpoints';
import { ERROR_MESSAGES } from '../../api/errorMsg';

const propTypes = {
    ...SectionProps.types
};

const defaultProps = {
    ...SectionProps.defaults
};

class ResetPasswordForm extends React.Component {
    constructor(props) {
        super(props);

        const emailFromLocation =
            (props.location &&
                props.location.state &&
                props.location.state.email) ||
            '';

        const emailFromStorage = localStorage.getItem('resetEmail') || '';

        this.state = {
            email: emailFromLocation || emailFromStorage || '',
            code: '',
            newPassword: '',
            confirmPassword: '',
            loading: false,
            error: '',
            success: '',
            showPasswordHint: false, // 👈 NEW: show rules when focusing new password
        };

        this.controller = null;
    }

    handleChange = (e) => {
        this.setState({
            [e.target.name]: e.target.value,
            error: '',
            success: ''
        });
    };

    // ✅ password strength check: 8+ chars, at least 1 number & 1 symbol
    validatePasswordStrength(pw) {
        if (typeof pw !== 'string') return false;
        const passwordRegex = /^(?=.*\d)(?=.*[^\w\s]).{8,}$/;
        return passwordRegex.test(pw);
    }

    handleSubmit = async (e) => {
        e.preventDefault();
        const { email, code, newPassword, confirmPassword } = this.state;

        // --- validations using central error messages ---
        if (!email) {
            this.setState({ error: ERROR_MESSAGES.RESET_PASSWORD.INVALID_EMAIL });
            return;
        }

        if (!code) {
            this.setState({ error: ERROR_MESSAGES.RESET_PASSWORD.INVALID_CODE });
            return;
        }

        if (!newPassword || !confirmPassword) {
            this.setState({
                error: ERROR_MESSAGES.RESET_PASSWORD.PASSWORD_REQUIRED
            });
            return;
        }

        if (newPassword !== confirmPassword) {
            this.setState({
                error: ERROR_MESSAGES.RESET_PASSWORD.PASSWORD_MISMATCH
            });
            return;
        }

        if (!this.validatePasswordStrength(newPassword)) {
            this.setState({
                error: ERROR_MESSAGES.RESET_PASSWORD.PASSWORD_WEAK
            });
            return;
        }

        const payload = {
            email,
            code,        // 👈 backend expects "code"
            newPassword, // 👈 backend expects "newPassword"
        };

        this.setState({ loading: true, error: "", success: "" });

        this.controller = new AbortController();
        const signal = this.controller.signal;

        try {
            const res = await api.post(
                API_ENDPOINTS.RESET_PASSWORD,
                payload,
                { signal }
            );

            const data = res.data;
            const successMsg =
                data?.message || ERROR_MESSAGES.RESET_PASSWORD.SUCCESS;

            this.setState({ success: successMsg, error: "" });

            // auto-redirect to login if enabled
            if (this.props.autoRedirectToLogin) {
                setTimeout(() => {
                    if (this.props.history && typeof this.props.history.push === "function") {
                        this.props.history.push("/login");
                    } else {
                        window.location.href = "/login";
                    }
                }, 1500);
            }
        } catch (err) {
            if (err.name === "CanceledError" || err.name === "AbortError") {
                console.log("Reset password request aborted");
            } else {
                let msg = ERROR_MESSAGES.RESET_PASSWORD.DEFAULT;

                if (err.response) {
                    const { status, data } = err.response;
                    const backendMsg = data?.message || data?.error;

                    if (backendMsg) {
                        msg = backendMsg;
                    } else if (status === 400 || status === 401) {
                        // e.g. invalid or expired OTP
                        msg = ERROR_MESSAGES.RESET_PASSWORD.EXPIRED_OR_INVALID_CODE;
                    }
                } else if (err.message === "Network Error") {
                    msg = ERROR_MESSAGES.RESET_PASSWORD.NETWORK_ERROR;
                }

                this.setState({ error: msg });
            }
        } finally {
            this.setState({ loading: false });
            this.controller = null;
        }
    };

    componentWillUnmount() {
        if (this.controller) this.controller.abort();
    }

    render() {
        const {
            className,
            topOuterDivider,
            bottomOuterDivider,
            topDivider,
            bottomDivider,
            hasBgColor,
            invertColor,
            ...props
        } = this.props;

        const {
            email,
            code,
            newPassword,
            confirmPassword,
            loading,
            error,
            success,
            showPasswordHint, // 👈 NEW
        } = this.state;

        const outerClasses = classNames(
            'signin section',
            topOuterDivider && 'has-top-divider',
            bottomOuterDivider && 'has-bottom-divider',
            hasBgColor && 'has-bg-color',
            invertColor && 'invert-color',
            className
        );

        const innerClasses = classNames(
            'signin-inner section-inner',
            topDivider && 'has-top-divider',
            bottomDivider && 'has-bottom-divider'
        );

        const sectionHeader = {
            title: 'パスワードを再設定',
            paragraph: 'メールで受け取った確認コード (OTP) と新しいパスワードを入力してください。'
        };

        return (
            <section {...props} className={outerClasses}>
                <div className="container">
                    <div className={innerClasses}>
                        <SectionHeader
                            tag="h2"
                            data={sectionHeader}
                            className="center-content"
                        />

                        <div className="tiles-wrap">
                            <div className="tiles-item">
                                <div className="tiles-item-inner">
                                    <form onSubmit={this.handleSubmit}>
                                        <fieldset>
                                            <div className="mb-12">
                                                <Input
                                                    type="email"
                                                    name="email"
                                                    label="メールアドレス"
                                                    placeholder="メールアドレス"
                                                    labelHidden
                                                    value={email}
                                                    onChange={this.handleChange}
                                                    required
                                                    readOnly
                                                    disabled   // user cannot change email
                                                />
                                            </div>

                                            <div className="mb-12">
                                                <Input
                                                    type="text"
                                                    name="code"
                                                    label="確認コード (OTP)"
                                                    placeholder="確認コード (OTP)"
                                                    labelHidden
                                                    value={code}
                                                    onChange={this.handleChange}
                                                    required
                                                />
                                            </div>

                                            <div className="mb-12">
                                                <Input
                                                    type="password"
                                                    name="newPassword"
                                                    label="新しいパスワード"
                                                    placeholder="新しいパスワード"
                                                    labelHidden
                                                    value={newPassword}
                                                    onChange={this.handleChange}
                                                    onFocus={() => this.setState({ showPasswordHint: true })}  // 👈 NEW
                                                    onBlur={() => this.setState({ showPasswordHint: false })}   // 👈 NEW
                                                    required
                                                />

                                                {showPasswordHint && (
                                                    <div className="hint-text text-xs mt-6">
                                                        パスワードは<strong>8文字以上</strong>で、
                                                        <strong>数字</strong>と<strong>記号</strong>を1つ以上
                                                        含める必要があります。
                                                    </div>
                                                )}
                                            </div>

                                            <div className="mb-12">
                                                <Input
                                                    type="password"
                                                    name="confirmPassword"
                                                    label="確認用パスワード"
                                                    placeholder="確認用パスワード"
                                                    labelHidden
                                                    value={confirmPassword}
                                                    onChange={this.handleChange}
                                                    required
                                                />
                                            </div>

                                            {error && (
                                                <div className="text-color-danger mb-12 text-xs">
                                                    {error}
                                                </div>
                                            )}
                                            {success && (
                                                <div className="text-color-success mb-12 text-xs">
                                                    {success}
                                                </div>
                                            )}

                                            <div className="mt-24 mb-32">
                                                <Button
                                                    type="submit"
                                                    color="primary"
                                                    wide
                                                    disabled={loading}
                                                >
                                                    {loading ? '送信中...' : 'パスワードを更新'}
                                                </Button>
                                            </div>

                                            <div className="signin-footer mb-32">
                                                <div className="footer-left">
                                                    <Link to="/login/" className="func-link text-xs">
                                                        ログインに戻る
                                                    </Link>
                                                </div>
                                                <div className="footer-right">
                                                    <Link to="/" className="func-link text-xs">
                                                        ホームへ戻る
                                                    </Link>
                                                </div>
                                            </div>
                                        </fieldset>
                                    </form>

                                    <div className="signin-bottom has-top-divider">
                                        <div className="pt-32 text-xs center-content text-color-low">
                                            コードが無効または期限切れの場合、
                                            再度{' '}
                                            <Link to="/forgot-password" className="func-link">
                                                パスワード再発行
                                            </Link>
                                            を行ってください。
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </section>
        );
    }
}

ResetPasswordForm.propTypes = propTypes;
ResetPasswordForm.defaultProps = defaultProps;

export default ResetPasswordForm;
